import React from 'react'
import {
  Typography,
  Snackbar,
  TableCell,
  TableBody,
  TableRow,
  Table,
  Box,
  TableHead,
  TableContainer,
  Paper,
  IconButton,
  Collapse,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import MuiAlert from '@mui/material/Alert'
import API from '@aws-amplify/api'
import { useDispatch } from 'react-redux'
import makeStyles from '@mui/styles/makeStyles'
import SmoothLoadingIndicator from 'components/shared/SmoothLoadingIndicator'
import {
  IPackagingRequest,
  PackagingRequest,
} from 'interfaces/IPackagingRequest'
import { formatTimestamp } from 'shared/dateFormatter'

const useStyles = makeStyles((theme) => ({
  labelColumn: {
    maxWidth: 'min-content',
  },
  expandableDetails: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  hiddenDetails: {
    paddingTop: 0,
  },
  productRow: {
    cursor: 'pointer',
  },
}))

export default function PackagingRequestList() {
  const dispatch = useDispatch()
  const classes = useStyles()

  const [packagingRequests, setPackagingRequests] = React.useState<
    IPackagingRequest[]
  >([])
  const [isLoading, setIsLoading] = React.useState<any>(true)
  const [snackbarState, setSnackbarState] = React.useState({
    isSuccessSnackbarOpen: false,
    isFailureSnackbarOpen: false,
    alertMessage: '',
  })

  const [expandedRows, setExpandedRows] = React.useState<{
    [key: string]: boolean
  }>({})

  const [cancelDialogOpen, setCancelDialogOpen] = React.useState(false)
  const [selectedRequest, setSelectedRequest] =
    React.useState<IPackagingRequest | null>(null)

  React.useEffect(() => {
    dispatch({ type: 'SET_PAGE_TITLE', payload: 'Daftar Request Kemasan' })
  }, [dispatch])

  React.useEffect(() => {
    loadPackagingRequests()
  }, [])

  const getConfirmedQuantity = (
    packagingRequest: PackagingRequest,
    packagingPartId: string
  ) => {
    const packagingPart = packagingRequest.stockCard?.stockCardPackaging.find(
      (packaging) => packaging.packagingPartId === packagingPartId
    )
    return packagingPart?.confirmedQuantity ?? null
  }

  const loadPackagingRequests = async () => {
    setIsLoading(true)

    let url = '/packaging/getPackagingRequests'
    const param = {
      queryStringParameters: {
        includeDetails: 'TRUE',
      },
    }

    try {
      const response = await API.get('products', url, param)
      response.sort((a: IPackagingRequest, b: IPackagingRequest) => {
        const dateA = new Date(a.createdAt ?? 0)
        const dateB = new Date(b.createdAt ?? 0)
        return dateB.getTime() - dateA.getTime()
      })
      setPackagingRequests(response)
    } catch (error) {
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Gagal memuat order.`,
        isFailureSnackbarOpen: true,
      }))
      console.error(error)
    }

    setIsLoading(false)
  }

  const handleOpenCancelDialog = (req: IPackagingRequest) => {
    setSelectedRequest(req)
    setCancelDialogOpen(true)
  }

  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false)
    setSelectedRequest(null)
  }

  const handleConfirmCancel = async () => {
    if (!selectedRequest) return
    try {
      setIsLoading(true)
      await API.post('products', '/packaging/cancelPackagingRequest', {
        body: {
          packagingRequestId: selectedRequest.packagingRequestId,
        },
      })
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Berhasil membatalkan request.`,
        isSuccessSnackbarOpen: true,
      }))
      await loadPackagingRequests()
    } catch (error) {
      console.error(error)
      setSnackbarState((prevState) => ({
        ...prevState,
        alertMessage: `Gagal membatalkan ${selectedRequest.packagingRequestDisplayId}`,
        isFailureSnackbarOpen: true,
      }))
    } finally {
      setIsLoading(false)
      handleCloseCancelDialog()
    }
  }

  const canCancel = (req: IPackagingRequest) => {
    return req.stockCard && !req.stockCard.isConfirmed
  }

  const getCancelReason = (req: IPackagingRequest) => {
    if (!req.stockCard) {
      return 'Tidak ada Kartu Stok terkait.'
    }
    if (req.stockCard.isConfirmed) {
      return 'Kartu Stok sudah dikonfirmasi.'
    }
    return null
  }

  return (
    <Box>
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="none"></TableCell>
                <TableCell>Nomor Request Kemasan</TableCell>
                <TableCell>Tanggal Request</TableCell>
                <TableCell>Nomor Dokumen Kartu Stok Masuk/Keluar</TableCell>
                <TableCell>In/Out</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Grid container justifyContent="center" alignItems="center">
                      <SmoothLoadingIndicator size={40} />
                    </Grid>
                  </TableCell>
                </TableRow>
              )}
              {packagingRequests.length === 0 && !isLoading && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography>Tidak ada request kemasan </Typography>
                  </TableCell>
                </TableRow>
              )}
              {packagingRequests.map((packagingRequest) => (
                <React.Fragment key={packagingRequest.packagingRequestId}>
                  <TableRow
                    className={classes.productRow}
                    hover
                    onClick={(e) => {
                      setExpandedRows({
                        ...expandedRows,
                        [packagingRequest.packagingRequestId]:
                          !expandedRows[packagingRequest.packagingRequestId],
                      })
                    }}
                  >
                    <TableCell padding="none">
                      <IconButton aria-label="expand row" size="small">
                        {expandedRows[packagingRequest.packagingRequestId] ? (
                          <KeyboardArrowUpIcon />
                        ) : (
                          <KeyboardArrowDownIcon />
                        )}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      {packagingRequest.packagingRequestDisplayId || '-'}
                    </TableCell>
                    <TableCell>
                      {formatTimestamp('id-ID', packagingRequest.createdAt)}
                    </TableCell>
                    <TableCell>
                      {packagingRequest.stockCard?.stockCardDisplayId ?? '-'}
                    </TableCell>
                    <TableCell>{packagingRequest.inOutDirection}</TableCell>
                    <TableCell>
                      {canCancel(packagingRequest) ? (
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenCancelDialog(packagingRequest)
                          }}
                        >
                          Batalkan
                        </Button>
                      ) : (
                        <Typography variant="caption" color="textSecondary">
                          {getCancelReason(packagingRequest)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell
                      colSpan={6}
                      className={
                        expandedRows[packagingRequest.packagingRequestId]
                          ? classes.hiddenDetails
                          : classes.expandableDetails
                      }
                    >
                      <Collapse
                        in={expandedRows[packagingRequest.packagingRequestId]}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box>
                          <Grid container>
                            <Grid item xs={12}>
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Kode Part Kemasan
                                      </Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Qty Diminta (pcs)
                                      </Box>
                                    </TableCell>
                                    <TableCell className={classes.labelColumn}>
                                      <Box fontWeight="bold">
                                        Qty Dikeluarkan (pcs)
                                      </Box>
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {packagingRequest.packagings.map(
                                    (packaging, index) => (
                                      <TableRow key={index}>
                                        <TableCell>
                                          {packaging.packagingPartId}
                                        </TableCell>
                                        <TableCell>{packaging.qty}</TableCell>
                                        <TableCell>
                                          {getConfirmedQuantity(
                                            packagingRequest,
                                            packaging.packagingPartId
                                          ) ?? 'Pending'}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            </Grid>
                          </Grid>
                          <Box mt={2}>
                            <Typography>
                              <Box fontWeight="bold" display="inline">
                                Notes:{' '}
                              </Box>
                              {packagingRequest.notes}
                            </Typography>
                          </Box>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Konfirmasi Pembatalan Request</DialogTitle>
        <DialogContent>
          {selectedRequest && (
            <Box>
              <Typography variant="body1" gutterBottom>
                Apakah anda yakin ingin membatalkan request berikut?
              </Typography>
              <Box mt={2} p={2} bgcolor="grey.100" borderRadius="4px">
                <Typography variant="body2">
                  <strong>Nomor Request:</strong>{' '}
                  {selectedRequest.packagingRequestDisplayId}
                </Typography>
                <Typography variant="body2">
                  <strong>Tanggal:</strong>{' '}
                  {formatTimestamp('id-ID', selectedRequest.createdAt)}
                </Typography>
                <Typography variant="body2">
                  <strong>Kartu Stok:</strong>{' '}
                  {selectedRequest.stockCard?.stockCardDisplayId ?? '-'}
                </Typography>

                <Typography variant="body2">
                  <strong>Arah:</strong> {selectedRequest.inOutDirection}
                </Typography>

                {selectedRequest.packagings &&
                  selectedRequest.packagings.length > 0 && (
                    <Box mt={2}>
                      <Typography variant="subtitle2" gutterBottom>
                        <strong>Detail Kemasan:</strong>
                      </Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Kode Part Kemasan</TableCell>
                            <TableCell>Qty Diminta (pcs)</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedRequest.packagings.map(
                            (packaging, index) => (
                              <TableRow key={index}>
                                <TableCell>
                                  {packaging.packagingPartId}
                                </TableCell>
                                <TableCell>{packaging.qty}</TableCell>
                              </TableRow>
                            )
                          )}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
              </Box>
              {getCancelReason(selectedRequest) && (
                <Box mt={2}>
                  <MuiAlert severity="warning">
                    Tidak dapat dibatalkan: {getCancelReason(selectedRequest)}
                  </MuiAlert>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog}>Batal</Button>
          {selectedRequest && canCancel(selectedRequest) && (
            <Button
              onClick={handleConfirmCancel}
              color="secondary"
              variant="contained"
              disabled={isLoading}
            >
              {isLoading ? 'Memproses...' : 'Konfirmasi Batalkan'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbarState.isSuccessSnackbarOpen}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbarState((prevState) => ({
            ...prevState,
            isSuccessSnackbarOpen: false,
          }))
        }
      >
        <MuiAlert severity="success" elevation={6} variant="filled">
          {snackbarState.alertMessage}
        </MuiAlert>
      </Snackbar>
      <Snackbar
        open={snackbarState.isFailureSnackbarOpen}
        autoHideDuration={6000}
        onClose={() =>
          setSnackbarState((prevState) => ({
            ...prevState,
            isFailureSnackbarOpen: false,
          }))
        }
      >
        <MuiAlert severity="error" elevation={6} variant="filled">
          {snackbarState.alertMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  )
}
